import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api, CURRENCIES, SUBSCRIPTION_CYCLES } from '../api.js';
import { useAuth } from '../auth.jsx';
import VendorPicker from '../components/VendorPicker.jsx';
import { useResizableColumns } from '../components/useResizableColumns.jsx';

function emptyForm() {
  return {
    name: '', vendor: '', vendorId: null, description: '',
    beneficiaryConcessionId: '', beneficiaryName: '',
    amountBeforeTax: '', amountAfterTax: '',
    currency: 'CAD', cycle: 'MONTHLY',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '', active: true, note: '',
  };
}

function formatMoney(amount, currency, opts = {}) {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency', currency: currency || 'CAD',
    maximumFractionDigits: opts.compact ? 0 : 2,
  }).format(Number(amount));
}

// Normalize a sub's amountAfterTax to a per-month figure (yearly subs / 12).
function monthlyOf(sub) {
  if (sub.amountAfterTax == null) return 0;
  const a = Number(sub.amountAfterTax);
  return sub.cycle === 'YEARLY' ? a / 12 : a;
}

function StatCard({ label, value, sublabel, accent = false }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent ? 'text-cyan-400' : 'text-zinc-100'}`}>
        {value}
      </div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1">{sublabel}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-emerald-500/80' : 'bg-zinc-700'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export default function Subscriptions() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'ADMIN';
  const subColumns = useMemo(() => {
    const cols = [
      { id: 'name', width: 220, min: 100 },
      { id: 'vendor', width: 180, min: 90 },
      { id: 'beneficiary', width: 150, min: 80 },
      { id: 'amount', width: 150, min: 90 },
      { id: 'cycle', width: 120, min: 80 },
      { id: 'active', width: 110, min: 80 },
    ];
    if (isAdmin) cols.push({ id: 'actions', width: 110, min: 90 });
    return cols;
  }, [isAdmin]);
  const { totalWidth, colGroup, resizer } = useResizableColumns('subs', subColumns, {
    resizerTitle: t('common.resizeHint'),
  });
  const [subs, setSubs] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive

  const reload = () => {
    api.get('/api/subscriptions')
      .then((res) => setSubs(res.data.subscriptions))
      .catch((err) => toast.error(err.message));
  };
  useEffect(() => {
    reload();
    api.get('/api/concessions').then((res) => setConcessions(res.data.concessions)).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const active = subs.filter((s) => s.active);
    const monthlyCAD = active.filter((s) => s.currency === 'CAD').reduce((sum, s) => sum + monthlyOf(s), 0);
    const monthlyUSD = active.filter((s) => s.currency === 'USD').reduce((sum, s) => sum + monthlyOf(s), 0);
    return {
      monthlyCAD,
      monthlyUSD,
      yearlyCAD: monthlyCAD * 12,
      activeCount: active.length,
      totalCount: subs.length,
    };
  }, [subs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subs.filter((s) => {
      if (activeFilter === 'active' && !s.active) return false;
      if (activeFilter === 'inactive' && s.active) return false;
      if (!q) return true;
      return (
        s.name?.toLowerCase().includes(q) ||
        s.vendor?.toLowerCase().includes(q) ||
        s.beneficiaryConcession?.code?.toLowerCase().includes(q) ||
        s.beneficiaryName?.toLowerCase().includes(q)
      );
    });
  }, [subs, search, activeFilter]);

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, vendor: s.vendor, vendorId: s.vendorId || null, description: s.description || '',
      beneficiaryConcessionId: s.beneficiaryConcession?.id || '',
      beneficiaryName: s.beneficiaryName || '',
      amountBeforeTax: s.amountBeforeTax ?? '',
      amountAfterTax: s.amountAfterTax ?? '',
      currency: s.currency, cycle: s.cycle,
      startDate: s.startDate.slice(0, 10),
      endDate: s.endDate ? s.endDate.slice(0, 10) : '',
      active: s.active, note: s.note || '',
    });
  };

  const startNew = () => { setEditing('new'); setForm(emptyForm()); };
  const cancel = () => { setEditing(null); setForm(emptyForm()); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        amountBeforeTax: form.amountBeforeTax === '' ? null : Number(form.amountBeforeTax),
        amountAfterTax: form.amountAfterTax === '' ? null : Number(form.amountAfterTax),
        vendorId: form.vendorId || null,
        beneficiaryConcessionId: form.beneficiaryConcessionId || null,
        beneficiaryName: form.beneficiaryName || null,
        startDate: new Date(form.startDate).toISOString(),
        endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
        description: form.description || null,
        note: form.note || null,
      };
      if (editing === 'new') {
        await api.post('/api/subscriptions', payload);
        toast.success(t('subs.created'));
      } else {
        await api.put(`/api/subscriptions/${editing.id}`, payload);
        toast.success(t('subs.updated'));
      }
      cancel();
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(t('subs.confirmDelete'))) return;
    try {
      await api.del(`/api/subscriptions/${id}`);
      toast.success(t('common.deleted'));
      reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="px-8 py-6 space-y-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('subs.title')}</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{t('subs.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/api/subscriptions/export.xlsx"
            className="btn-ghost border border-zinc-800"
            title={t('subs.exportXlsx')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t('subs.exportXlsx')}
          </a>
          {isAdmin && <button onClick={startNew} className="btn-primary">{t('subs.new')}</button>}
        </div>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label={t('subs.stat.monthlyCAD')}
          value={formatMoney(stats.monthlyCAD, 'CAD')}
          sublabel={t('subs.monthlyEquiv')}
          accent
        />
        <StatCard
          label={t('subs.stat.monthlyUSD')}
          value={formatMoney(stats.monthlyUSD, 'USD')}
          sublabel={t('subs.monthlyEquiv')}
        />
        <StatCard
          label={t('subs.stat.yearlyCAD')}
          value={formatMoney(stats.yearlyCAD, 'CAD', { compact: true })}
        />
        <StatCard
          label={t('subs.stat.activeCount')}
          value={stats.activeCount}
          sublabel={t('subs.ofTotal', { total: stats.totalCount })}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="search"
          placeholder={t('subs.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-xs"
        />
        <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900/40 p-0.5 text-xs">
          {[
            { id: 'all',      label: t('subs.filter.all') },
            { id: 'active',   label: t('subs.filter.activeOnly') },
            { id: 'inactive', label: t('subs.filter.inactiveOnly') },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`px-2.5 py-1 rounded ${
                activeFilter === f.id
                  ? 'bg-zinc-800 text-zinc-100'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-zinc-500 tabular-nums">
          {filtered.length} / {subs.length}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <form onSubmit={submit} className="card p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="label">{t('subs.form.name')}</label>
              <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="label">{t('subs.form.vendor')}</label>
              <VendorPicker
                value={form.vendor}
                vendorId={form.vendorId}
                onChange={(vendor, vendorId) => setForm({ ...form, vendor, vendorId })}
                required
              /></div>
          </div>
          <div>
            <label className="label">{t('subs.form.description')}</label>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">{t('subs.form.beneficiaryConcession')}</label>
              <select value={form.beneficiaryConcessionId} onChange={(e) => setForm({ ...form, beneficiaryConcessionId: e.target.value })} className="input">
                <option value="">—</option>
                {concessions.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">{t('subs.form.beneficiaryName')}</label>
              <input className="input" value={form.beneficiaryName} onChange={(e) => setForm({ ...form, beneficiaryName: e.target.value })} placeholder="ex: Ressources Humaines" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="label">{t('subs.form.amountBeforeTax')}</label>
              <input type="number" step="0.01" min={0} className="input" value={form.amountBeforeTax} onChange={(e) => setForm({ ...form, amountBeforeTax: e.target.value })} /></div>
            <div><label className="label">{t('subs.form.amountAfterTax')}</label>
              <input type="number" step="0.01" min={0} className="input" value={form.amountAfterTax} onChange={(e) => setForm({ ...form, amountAfterTax: e.target.value })} /></div>
            <div><label className="label">{t('subs.form.currency')}</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="label">{t('subs.form.cycle')}</label>
              <select value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })} className="input">
                {SUBSCRIPTION_CYCLES.map((c) => <option key={c} value={c}>{t(`subs.cycle.${c}`)}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="label">{t('subs.form.startDate')}</label>
              <input type="date" required className="input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></div>
            <div><label className="label">{t('subs.form.endDate')}</label>
              <input type="date" className="input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></div>
            <div className="flex items-end pb-1.5 gap-2">
              <Toggle checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-xs text-zinc-400">{t('subs.form.active')}</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={cancel} className="btn-ghost">{t('common.cancel')}</button>
            <button type="submit" disabled={busy} className="btn-primary">{busy ? t('common.saving') : t('common.save')}</button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="text-sm table-fixed" style={{ width: totalWidth, minWidth: '100%' }}>
          {colGroup}
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="relative px-4 py-2.5 text-left"><span className="block truncate pr-2">{t('subs.col.name')}</span>{resizer(0)}</th>
              <th className="relative px-4 py-2.5 text-left"><span className="block truncate pr-2">{t('subs.col.vendor')}</span>{resizer(1)}</th>
              <th className="relative px-4 py-2.5 text-left"><span className="block truncate pr-2">{t('subs.col.beneficiary')}</span>{resizer(2)}</th>
              <th className="relative px-4 py-2.5 text-right"><span className="block truncate pl-2">{t('subs.col.amount')}</span>{resizer(3)}</th>
              <th className="relative px-4 py-2.5 text-left"><span className="block truncate pr-2">{t('subs.col.cycle')}</span>{resizer(4)}</th>
              <th className="relative px-4 py-2.5 text-center"><span className="block truncate">{t('subs.col.active')}</span>{isAdmin && resizer(5)}</th>
              {isAdmin && <th className="px-4 py-2.5"></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-12 text-center text-zinc-500">
                {subs.length === 0 ? t('subs.empty') : t('subs.emptyFiltered')}
              </td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} className="group border-t border-zinc-800 hover:bg-zinc-900/60 transition-colors">
                <td className="px-4 py-3 font-medium truncate" title={s.name}>{s.name}</td>
                <td className="px-4 py-3 text-zinc-400 truncate" title={s.vendor}>{s.vendor}</td>
                <td className="px-4 py-3 text-zinc-400 truncate">
                  {s.beneficiaryConcession ? (
                    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] bg-zinc-800 text-zinc-300">
                      {s.beneficiaryConcession.code}
                    </span>
                  ) : (s.beneficiaryName || '—')}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">
                  {formatMoney(s.amountAfterTax, s.currency)}
                  <span className="ml-1 text-[10px] text-zinc-500">{s.currency}</span>
                </td>
                <td className="px-4 py-3 text-zinc-400 truncate">{t(`subs.cycle.${s.cycle}`)}</td>
                <td className="px-4 py-3 text-center">
                  {s.active
                    ? <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] bg-emerald-900/30 text-emerald-300 border border-emerald-800/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />{t('common.active')}
                      </span>
                    : <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] bg-zinc-800 text-zinc-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />{t('common.inactive')}
                      </span>}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex gap-3">
                      <button onClick={() => startEdit(s)} className="text-xs text-zinc-400 hover:text-zinc-100">{t('common.edit')}</button>
                      <button onClick={() => remove(s.id)} className="text-xs text-rose-400 hover:text-rose-300">{t('common.delete')}</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
